import { Head, router, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { productsPath, profilePath, profileStorePath, servicesPath } from "@travily/routes";
import type CommunitiesStore from "@travily/types/pages/CommunitiesStore";
import { SwooshBackground } from "@ui/components/Swoosh";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { useShare } from "@ui/hooks/use-share";
import { PROFILE_SWOOSH_COLORS } from "@/components/ProfileFrame/ProfileFrame";
import { communityProfileAccount } from "@/components/ProfileFrame/profile-account";
import { ProfileStoreView } from "@/components/ProfileStoreView/ProfileStoreView";
import { useFriendshipActions } from "@/hooks/use-friendship-actions";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { PageFrame } from "@/layouts/page-frame";
import type { SharedProps } from "@/types";

const CommunityStorePage = ({ community, products, services, can }: CommunitiesStore) => {
  const { share } = useShare();
  const { current_account } = usePage<SharedProps>().props;
  const isOwnProfile = current_account?.handle === community.handle;
  const onBack = useNavigateBack(profilePath(community.handle));
  const friendshipActions = useFriendshipActions(community);

  const handleShareProfile = () => {
    share({
      title: community.display_name,
      text: t("web.communities.show.share_text", { name: community.display_name }),
      url: `${location.origin}${profilePath(community.handle)}`,
    });
  };

  return (
    <PageFrame
      background={<SwooshBackground className="hidden md:block" color={PROFILE_SWOOSH_COLORS.Community} />}
      mobileTopBar={isOwnProfile ? undefined : <TopBarMobile onBack={onBack} title={`@${community.handle}`} />}
    >
      <Head title={t("web.communities.store.meta_title", { name: community.display_name })}>
        <meta
          name="description"
          content={t("web.communities.show.meta_description", { name: community.display_name })}
        />
      </Head>
      <ProfileStoreView
        account={communityProfileAccount(community)}
        canCreateListing={can.create_listing}
        onAddProduct={can.create_listing ? () => router.post(productsPath()) : undefined}
        onAddService={can.create_listing ? () => router.post(servicesPath()) : undefined}
        onShareProfile={handleShareProfile}
        products={products}
        services={services}
        tabs={{ profileHref: profilePath(community.handle), storeHref: profileStorePath(community.handle) }}
        {...friendshipActions}
      />
    </PageFrame>
  );
};

export default CommunityStorePage;
